import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import React, { useEffect, useState } from 'react';
import { useAppSelector } from '../../app/hooks';
import { selectSessionId } from '../../redux/features/sessionBoundary/sessionBoundary.selectors';
import {
  selectActiveRestaurantCode,
  selectShowTaskRouterOnBanner,
} from '../../selectors/restaurant';
import Colors from '../../utils/color';
import FloatingBanner, { BannerActionButton } from '../common/FloatingBanner';

// Geometry and button styling come from the shared FloatingBanner (HITL-1499).
// Amber accent (rather than RefreshBanner's blue/red actions) because this is an
// advisory, not something the agent must act on.
const useStyles = makeStyles<Theme>(() => ({
  message: {
    fontSize: '0.875rem',
    lineHeight: 1.4,
  },
}));

/**
 * HITL-1459: warns an agent who reached a task-router-enabled restaurant by
 * picking it from the dropdown that an order here belongs to whoever the task
 * router assigns it to. Advisory only — nothing on the page is disabled.
 */
const TaskRouterOnBanner = () => {
  const { message } = useStyles();
  const showBanner = useAppSelector(selectShowTaskRouterOnBanner);
  const restaurantCode = useAppSelector(selectActiveRestaurantCode);
  const sessionId = useAppSelector(selectSessionId);
  // Remember WHICH restaurant was dismissed, not merely that a dismissal
  // happened — switching to a different task-router store is a new warning.
  const [dismissedCode, setDismissedCode] = useState('');

  // Drop the acknowledgement on every new car (sessionId changes on car_enter)
  // and whenever the advisory stops applying. A dismissal only ever covers the
  // order in front of the agent: the next car is a fresh chance to step on an
  // order the task router assigned to somebody else, which is the whole point
  // of the warning, so it has to be raised again.
  useEffect(() => {
    setDismissedCode('');
  }, [sessionId, showBanner]);

  if (!showBanner || dismissedCode === restaurantCode) return null;

  return (
    // data-task-router-banner keeps this out of agent-interception detection
    // (AGENT_INTERCEPTION_IGNORED_SELECTORS). Both guards listen on document in
    // the capture phase, so stopping the event on the button would not work —
    // and acknowledging a "don't touch this order" warning must never be read
    // as taking the order over. It has to sit on the ROOT, which is why
    // FloatingBanner spreads unknown props there.
    <FloatingBanner
      accent={Colors.amber}
      icon={<WarningAmberIcon />}
      maxWidth={640}
      data-task-router-banner="true"
      data-testid="task-router-on-banner"
      actions={
        <BannerActionButton onClick={() => setDismissedCode(restaurantCode)}>
          Got it
        </BannerActionButton>
      }
    >
      <span className={message}>
        Task Router is ON for this restaurant — do not take orders here.
      </span>
    </FloatingBanner>
  );
};

export default TaskRouterOnBanner;
